import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { ActionsTableRow } from '@Components/MainTable/MainTable.styled';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdActiveStatus from '@Components/UI/Utilities/EdActiveStatus/EdActiveStatus';
import EdEmptyValue from '@Components/UI/Utilities/EdEmptyValue/EdEmptyValue';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import EdSpeedDial, {
	EdSpeedDialAction,
} from '@Components/UI/Utilities/EdSpeedDial/EdSpeedDial';
import { useTypedSelector } from '@Features/store';
import { Button, TableCell, Tooltip } from '@mui/material';
import DeleteModule from '@Pages/ScratchCards/components/DeleteModeule';
import GenerateCodes from '@Pages/ScratchCards/components/GenerateCode';
import {
	scratchCardsDeleteById,
	scratchCardsExportData,
	scratchCardsGetAll,
} from '@Pages/ScratchCards/Slices/ScratchCards.slice';
import { ScratchCard } from '@Pages/ScratchCards/Types/ScratchCards.types';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout } from '@Styled/utilities';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import usePlatformMode from '@Hooks/usePlatformMode';
import {
	CardTargetChip,
	resolveTargetType,
} from '@Pages/ScratchCards/components/CardTarget';
import BulkEditModule from '@Pages/ScratchCards/components/BulkEditModule';
import EditCode from '@Pages/ScratchCards/components/EditCode';
import { resetScratchcard } from '@Pages/ScratchCards/Services/ScratchCards.req';

/**
 * TARGET and PRODUCT replace the old CLASSROOM/COURSE pair, which rendered blank
 * for standalone and bundle cards. CLASSROOM is kept as extra context, but only
 * when the platform actually runs classrooms.
 */
const buildTableHeads = (classroomsEnabled: boolean): Array<TableHeader> => [
	{ displayName: 'SERIAL', fieldName: 'serial', canSort: true },
	{ displayName: 'CODE', fieldName: 'code', canSort: true },
	{ displayName: 'BATCH', fieldName: 'batch' },
	{ displayName: 'TARGET', fieldName: 'target_type' },
	{ displayName: 'PRODUCT', fieldName: 'product_label' },
	...(classroomsEnabled
		? [{ displayName: 'CLASSROOM ', fieldName: 'title' }]
		: []),
	{ displayName: 'INSTRUCTOR ', fieldName: 'instructor' },
	{ displayName: 'VALUE ', fieldName: 'value' },
	{ displayName: 'USED', fieldName: 'user_id', canSort: false },
	{ displayName: 'USED AT', fieldName: 'updated_at', width: '12%' },
	{ displayName: 'PHONE NUMBER', fieldName: 'phone_number', width: '10%' },
	{ displayName: 'USERNAME', fieldName: 'username', width: '10%' },
	{ displayName: 'CREATED AT', fieldName: 'created_at', width: '12%' },
];
const ScratchCardsTable = () => {
	const dispatch = useDispatch();
	const { classroomsEnabled } = usePlatformMode();
	const TABLE_HEADS = useMemo(
		() => buildTableHeads(classroomsEnabled),
		[classroomsEnabled]
	);
	const onPageRequestChange = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
			export: exportData,
		}: onPageChangeRequestMeta) => {
			dispatch(
				scratchCardsGetAll({
					page: activePage,
					filters: filters,
					perPage: perPage ?? 10,
					query,
					export: exportData ?? false,
					sortBy: sortBy,
					from: startDate,
					to: endDate,
				})
			);
		},
		[dispatch]
	);

	const { fetchAll, filters, meta, selectedFilters, dateRange, query, sortBy } =
		useTypedSelector((state) => state.ScratchCards);
	const auth = useTypedSelector((state) => state.auth);

	const { dropdownClassrooms } = useTypedSelector((state) => state.dropdowns);

	const [openDeleteModel, setOpenDeleteModel] = useState(false);
	const [openModel, setOpenModel] = useState(false);
	const [openBulkEditModel, setOpenBulkEditModel] = useState(false);
	const [toBeDeleted, setToBeDelete] = useState<number | undefined>();
	const [cardToEdit, setCardToEdit] = useState<ScratchCard | null>(null);
	const [cardToReset, setCardToReset] = useState<ScratchCard | null>(null);

	const handleDeleteModelOpen = () => {
		setOpenDeleteModel(true);
	};
	const handleModelClose = () => {
		setOpenModel(false);
	};
	const handleDeleteModelClose = () => {
		setOpenDeleteModel(false);
	};

	const handleDeleteClick = (id?: number) => {
		setOpenConfirmModal(true);
		setToBeDelete(id);
	};

	const refetchList = useCallback(() => {
		dispatch(
			scratchCardsGetAll({
				page: meta?.current_page ?? 1,
				query: query ?? '',
				sortBy,
				filters: selectedFilters,
				perPage: meta?.per_page ?? 10,
				from: dateRange.from,
				to: dateRange.to,
				export: false,
			})
		);
	}, [dispatch, meta, query, sortBy, selectedFilters, dateRange]);

	// Reset frees a redeemed classroom-course card: it un-enrolls the student,
	// wipes their course progress and removes the sale's financial snapshot, so
	// it gets its own confirm modal rather than a bare click.
	const handleCloseResetModal = async (close: boolean, confirm?: boolean) => {
		setOpenResetConfirm(close);
		if (confirm && cardToReset?.id && cardToReset.user_id) {
			try {
				await resetScratchcard({
					id: cardToReset.id,
					user_id: Number(cardToReset.user_id),
				});
				displaySnackbar('success', 'Scratch card reset successfully');
				refetchList();
			} catch (error: any) {
				displaySnackbar(
					'error',
					error?.response?.data?.message ?? 'Unable to reset scratch card'
				);
			}
		}
		setCardToReset(null);
	};
	const [openResetConfirm, setOpenResetConfirm] = useState(false);
	const handleCopy = async (item: ScratchCard) => {
		const itemString = Object.keys(item).reduce((acc, key, index) => {
			if (!!item[key as keyof ScratchCard]) {
				return `${acc} ,${key}: ${item[key as keyof ScratchCard]}`;
			}
			return acc;
		});

		await navigator.clipboard.writeText(itemString);
		displaySnackbar('success', 'Code copied to clipboard');
	};

	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const { displaySnackbar } = useSnackbar();

	const handleCloseDeleteModal = (close: boolean, confirm?: boolean) => {
		setOpenConfirmModal(close);
		if (confirm && toBeDeleted) {
			try {
				dispatch(scratchCardsDeleteById(toBeDeleted));
				displaySnackbar('success', 'Scratch card deleted successfully');
			} catch (error) {}
		}
		setToBeDelete(undefined);
	};
	return (
		<div>
			<GenerateCodes
				classRooms={dropdownClassrooms}
				openModel={openModel}
				handleModelClose={handleModelClose}
				onConfirmation={handleModelClose}
			/>
			<DeleteModule
				onConfirmation={handleDeleteModelClose}
				openModel={openDeleteModel}
				handleModelClose={handleDeleteModelClose}
			/>
			<BulkEditModule
				classRooms={dropdownClassrooms}
				openModel={openBulkEditModel}
				handleModelClose={() => setOpenBulkEditModel(false)}
			/>
			<EditCode
				classRooms={dropdownClassrooms}
				openModel={cardToEdit !== null}
				handleModelClose={() => setCardToEdit(null)}
				card={cardToEdit}
			/>

			<EdConfirmModal
				open={openConfirmModal}
				handleClose={handleCloseDeleteModal}
				text={'Are you sure to delete scratch card ?'}
			/>
			<EdConfirmModal
				open={openResetConfirm}
				handleClose={handleCloseResetModal}
				text={
					'Reset this scratch card? The student will be un-enrolled from the course, their progress wiped, and the card becomes sellable again.'
				}
			/>
			<FlexLayout justifyContent="flex-end">
				{(auth.user.type === 'super' || auth.permissions.has('exp-schc')) && (
					<RoundButton
						onClick={() => {
							dispatch(
								scratchCardsExportData({
									filters: selectedFilters,
									page: meta?.current_page ?? 1,
									perPage: meta?.per_page ?? 10,
									query: query ?? '',
									sortBy,
									from: dateRange.from,
									to: dateRange.to,
								})
							);
						}}
						sx={{ marginRight: '1rem' }}
						startIcon={<EdIcon>south</EdIcon>}
						variant="contained"
					>
						Export
					</RoundButton>
				)}
				{(auth.user.type === 'super' || auth.permissions.has('gen-schc')) && (
					<RoundButton
						edcolor="purple"
						startIcon={<EdIcon>add</EdIcon>}
						variant="contained"
						onClick={() => setOpenModel(true)}
					>
						Generate codes
					</RoundButton>
				)}
			</FlexLayout>
			<CompactHeaders>
			<MainTable
				tableFilters={filters}
				tableHeads={TABLE_HEADS}
				totalPages={meta?.last_page ?? 0}
				onPageChangeRequest={onPageRequestChange}
				RowsperPage={meta?.per_page}
				$hasActions
				pageNum={meta?.current_page}
				total={meta?.total}
				renderItems={() => {
					if (fetchAll.length === 0) {
						return (
							<MainTableRow>
								<TableCell colSpan={TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					} else {
						return fetchAll.map((code) => {
							return (
								<MainTableRow key={code.id}>
									<Tooltip title={code.serial} placement="top">
										<TableCell>{code.serial}</TableCell>
									</Tooltip>
									<Tooltip title={code.code} placement="top">
										<TableCell>{code.code}</TableCell>
									</Tooltip>
									<TableCell>{code.batch}</TableCell>
									<TableCell>
										<CardTargetChip target={resolveTargetType(code)} />
									</TableCell>
									<Tooltip title={code.product_label ?? ''} placement="top">
										<TableCell>{code.product_label ?? '-'}</TableCell>
									</Tooltip>
									{classroomsEnabled && (
										<Tooltip title={code.title ?? ''} placement="top">
											<TableCell>{code.title ?? '-'}</TableCell>
										</Tooltip>
									)}
									<TableCell>{code.instructor_label ?? '-'}</TableCell>
									<TableCell>{code.value ?? '-'}</TableCell>
									<TableCell>
										<EdActiveStatus
											active={code.user_id !== null ? true : false}
										/>
									</TableCell>
									{/* Empty title = no tooltip: an unused card's updated_at is
									    just its generation (or retarget) time, not a used-at. */}
									<Tooltip
										title={
											code.user_id !== null ? (
												<DateTimeFormatter date={code.updated_at} />
											) : (
												''
											)
										}
										placement="top"
									>
										<TableCell>
											{code.user_id !== null ? (
												<DateTimeFormatter date={code.updated_at} />
											) : (
												'-'
											)}
										</TableCell>
									</Tooltip>
									<TableCell>
										<EdEmptyValue value={code.phone_number} />
									</TableCell>
									<TableCell>
										<EdEmptyValue value={code.username} />
									</TableCell>
									<Tooltip
										title={<DateTimeFormatter date={code.created_at} />}
										placement="top"
									>
										<TableCell>
											<DateTimeFormatter date={code.created_at} />
										</TableCell>
									</Tooltip>
									<ActionsTableRow>
										{(auth.user.type === 'super' ||
											auth.permissions.has('d-schc') ||
											auth.permissions.has('u-schc') ||
											auth.permissions.has('r-schc')) && (
											<EdSpeedDial ariaLabel="Actions">
												{[
													(auth.user.type === 'super' ||
														auth.permissions.has('d-schc')) && (
														<EdSpeedDialAction
															onClick={() => handleDeleteClick(code.id)}
															key="delete"
															tooltipTitle="Delete"
															icon={<EdIcon>delete</EdIcon>}
															aria-label=""
														/>
													),
													// Edit retargets, and a used card's target is a record
													// of what was bought — unused cards only.
													(auth.user.type === 'super' ||
														auth.permissions.has('u-schc')) &&
														code.user_id === null && (
															<EdSpeedDialAction
																onClick={() => setCardToEdit(code)}
																key="edit"
																tooltipTitle="Edit"
																icon={<EdIcon>edit</EdIcon>}
																aria-label=""
															/>
														),
													// The backend supports reset for redeemed
													// classroom-course cards only.
													(auth.user.type === 'super' ||
														auth.permissions.has('r-schc')) &&
														code.user_id !== null &&
														!!code.course_id && (
															<EdSpeedDialAction
																onClick={() => {
																	setCardToReset(code);
																	setOpenResetConfirm(true);
																}}
																key="reset"
																tooltipTitle="Reset"
																icon={<EdIcon>restart_alt</EdIcon>}
																aria-label=""
															/>
														),
													<EdSpeedDialAction
														onClick={() => handleCopy(code)}
														key="copy"
														tooltipTitle="Copy"
														icon={<EdIcon>content_copy</EdIcon>}
														aria-label=""
													/>,
												].filter(Boolean)}
											</EdSpeedDial>
										)}
									</ActionsTableRow>
								</MainTableRow>
							);
						});
					}
				}}
			/>
			</CompactHeaders>
			<FlexLayout justifyContent="flex-end" gridGap="1rem">
				{(auth.user.type === 'super' || auth.permissions.has('u-schc')) && (
					<Button
						variant="contained"
						onClick={() => setOpenBulkEditModel(true)}
					>
						Edit Codes
					</Button>
				)}
				{(auth.user.type === 'super' || auth.permissions.has('d-schc')) && (
					<Button
						variant="contained"
						color="warning"
						onClick={handleDeleteModelOpen}
					>
						Delete Codes
					</Button>
				)}
			</FlexLayout>
		</div>
	);
};

export default ScratchCardsTable;

/* Thirteen columns: MainTable's 1rem nowrap uppercase headers collide, so this
   table alone gets compact header typography (and tighter cell padding) —
   same treatment as the video-imports table. */
const CompactHeaders = styled.div`
	th.MuiTableCell-head {
		font-size: 0.78rem;
		letter-spacing: 0.03em;
		padding-left: 0.4rem;
		padding-right: 0.4rem;
	}

	td.MuiTableCell-root {
		padding-left: 0.4rem;
		padding-right: 0.4rem;
	}
`;
